import { useState } from "react";

import { Button, Card } from "react-bootstrap";
import { toast } from "react-toastify";

import { useMutation } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { Divider } from "primereact/divider";

import ModalValidation from "./ModalValidation.tsx";
import { AppointmentStatusSlug } from "../../../_constants/appointment.ts";
import { getServiceOrderTypeValuesByType } from "../../../_constants/serviceOrder.ts";
import { getICSData } from "../../../_helpers/agendaHelper.ts";
import {
  dateFormatIcal,
  dateFormatWeekday,
  timeFormat,
} from "../../../_helpers/dateHelper.ts";
import Agenda from "../../../assets/svg/Agenda.tsx";
import checkBig from "../../../assets/svg/checkBig.svg";
import Chrono from "../../../assets/svg/Chrono.tsx";
import { Question } from "../../../assets/svg/Question.tsx";
import { IDwelling, IServiceAgency } from "../../dwelling/reducers";
import { IService } from "../../serviceOrdering/reducers";
import { IConfirmationAppointment } from "../reducers";
import { confirmAppointment, confirmAppointmentStatus } from "../services";


interface EntryFormComponentProps {
  appointment: IConfirmationAppointment;
  service: IService;
  dwelling: IDwelling;
  agency: IServiceAgency;
}

export const ConfirmationForm = ({
  service,
  appointment,
  dwelling,
  agency,
}: EntryFormComponentProps) => {
  const [validationModal, setValidationModal] = useState(false);
  const [isValidate, setIsValidate] = useState(false);

  enum color {
    green = "#11BFA4",
    greenLight = "rgba(17, 191, 164, 0.10)",
    white = "#FFFFFF",
  }

  const serviceTypeLabel =
    getServiceOrderTypeValuesByType(service.type)?.label ?? "Service inconnu";

  const appointmentMutation = useMutation({
    mutationFn: () =>
      appointment.lastStatus.status.slug === AppointmentStatusSlug.CONFIRMED
        ? confirmAppointment(appointment.id)
        : confirmAppointmentStatus(appointment.id),
    onError: (error) => {
      console.error(error);
      toast.error(
        "Une erreur est survenue lors de la confirmation du rendez-vous. Veuillez réessayer plus tard.",
      );
    },
    onSuccess: async () => setIsValidate(true),
  });

  const addToAgenda = () => {
    const icsContent = getICSData(
      `${serviceTypeLabel}`,
      "",
      service?.dwelling?.addressLabel,
      DateTime.fromISO(appointment?.startDate).toFormat(dateFormatIcal),
      DateTime.fromISO(appointment?.endDate).toFormat(dateFormatIcal),
    );
    window.open(
      `data:text/calendar;charset=utf8,${encodeURIComponent(icsContent)}`,
    );
  };

  const handleClickButton = () =>
    isValidate ? addToAgenda() : setValidationModal(true);

  return (
    <>
      <ModalValidation
        address={dwelling?.addressLabel}
        appointmentDateTime={appointment.startDate}
        confirmAppointment={() => appointmentMutation.mutate()}
        setShow={setValidationModal}
        show={validationModal}
        setIsValidate={setIsValidate}
        service={serviceTypeLabel}
      />

      {isValidate ? (
        <div
          className={
            "p-3 rounded mb-3 d-flex align-items-center justify-content-center"
          }
          style={{ background: color.greenLight }}
        >
          <img
            src={checkBig}
            className={"me-3"}
            style={{ color: color.green }}
          />
          <div>
            <h2 className={"mb-0"} style={{ color: color.green }}>
              Merci !
            </h2>
            <p className={"mb-0"}>Votre rendez-vous a bien été confirmé</p>
          </div>
        </div>
      ) : (
        <div
          className={"p-3 rounded mb-3"}
          style={{
            background: color.greenLight,
            color: color.green,
          }}
        >
          L’agence <strong>{agency?.label}</strong> vous invite à confirmer
          votre rendez-vous avec un de nos experts d’état des lieux.
        </div>
      )}

      <Divider />

      <Card className="pt-auto mb-3 border-0">
        <Card.Header className="d-flex align-items-center justify-content-between flex-wrap bg-primary text-white py-3">
          <div>
            <Agenda color={color.white} h={20} w={20} className={"me-2"} />
            <span>
              {DateTime.fromISO(appointment.startDate).toFormat(
                dateFormatWeekday,
              )}
            </span>
          </div>
          <span>
            {DateTime.fromISO(appointment.startDate).toFormat(timeFormat)}
          </span>
        </Card.Header>
        <Card.Body>
          <div className="d-flex flex-row justify-content-between">
            <p>
              <strong>{serviceTypeLabel}</strong>
              <br />
              <strong>#{service.datedReference}</strong>
              <br />
              {dwelling.address.address.address}, {dwelling.address.address.zip}{" "}
              {dwelling.address.address.city}
            </p>
            <div>
              <Chrono />
              <span className={"text-primary ms-1"}>1h</span>
            </div>
          </div>
          <Button
            type="button"
            variant="success"
            className="py-3 btn-add-new w-100"
            onClick={handleClickButton}
          >
            {isValidate ? "Ajouter à mon agenda" : "Confirmer le rendez-vous"}
          </Button>
        </Card.Body>
      </Card>

      <div className={"p-2 rounded mb-3 d-flex align-items-center bg-white"}>
        <Question className={"ms-2 me-3"} />
        <p className={"mb-0"}>
          Vous ne pourrez pas être sur place ? Contactez notre support en
          cliquant sur la bulle ci-dessous
        </p>
      </div>
    </>
  );
};
